import type { Light, LightArrangement } from "./vesselIdentity.js";
import { colourFor } from "./vesselIdentity.js";

/**
 * Light-arrangement diagram. Renders four perspectives so a navigator
 * can see exactly where every light sits on the vessel:
 *
 *   - **Plan view** (top-down): port red on the LEFT, stbd green on
 *     the RIGHT. Sidelight visibility wedges show which colour you'd
 *     observe from any bearing.
 *   - **Side view** (looking at port side): bow on the right, stern
 *     on the left, mast forward of amidships. Vertical stacks of
 *     all-round lights are unambiguously stacked on the mast.
 *   - **Bow view** (vessel pointing at you, "head-on aspect"): port
 *     red on YOUR RIGHT (the vessel's left), stbd green on YOUR LEFT.
 *     Vertical stack visible above the masthead.
 *   - **Stern view** (vessel going away, "stern aspect"): sternlight
 *     centre, port red on YOUR LEFT, stbd green on YOUR RIGHT (matches
 *     the plan view because you're looking the same direction as
 *     the vessel).
 *
 * The four views are the standard navigation-training set — they show
 * what you'd see by eye from any quadrant.
 */
export function LightArrangementDiagram(props: {
  arrangement: LightArrangement;
  width?: number;
}): JSX.Element {
  const w = props.width ?? 92;
  const halfW = Math.floor((w - 4) / 2);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <PlanView arrangement={props.arrangement} width={w} />
      <SideView arrangement={props.arrangement} width={w} />
      <div style={{ display: "flex", gap: 4 }}>
        <BowView arrangement={props.arrangement} width={halfW} />
        <SternView arrangement={props.arrangement} width={halfW} />
      </div>
    </div>
  );
}

function PlanView(props: {
  arrangement: LightArrangement;
  width: number;
}): JSX.Element {
  const w = props.width;
  const h = w * 1.45;
  // Coordinate system: 0,0 = top-left of viewBox; bow at top centre.
  // Vessel outline runs roughly from y=8 (bow tip) to y=h-12 (stern).
  const cx = w / 2;
  const bowY = 12;
  const sternY = h - 14;
  const beam = w * 0.32;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      role="img"
      aria-label="Navigation light arrangement"
    >
      {/* Sidelight visibility arcs — 112.5° each, from dead ahead to
          22.5° abaft beam. Drawn before the hull so the hull paints
          on top. */}
      <SidelightArcs cx={cx} cy={bowY + (sternY - bowY) * 0.35} radius={w * 0.42} />
      {/* Hull silhouette (top-down). Pointed bow, flat stern. */}
      <path
        d={hullPath(cx, bowY, sternY, beam)}
        fill="rgba(154, 166, 184, 0.18)"
        stroke="rgba(154, 166, 184, 0.55)"
        strokeWidth="1"
      />
      {/* Centreline + label tick marks for orientation. */}
      <line
        x1={cx}
        y1={bowY - 3}
        x2={cx}
        y2={sternY + 3}
        stroke="rgba(154, 166, 184, 0.18)"
        strokeWidth="0.5"
        strokeDasharray="2 2"
      />
      <text
        x={cx}
        y={6}
        fontSize="7"
        textAnchor="middle"
        fill="var(--text-dim)"
        fontFamily="var(--font-mono)"
      >
        BOW
      </text>
      <text
        x={cx}
        y={h - 3}
        fontSize="7"
        textAnchor="middle"
        fill="var(--text-dim)"
        fontFamily="var(--font-mono)"
      >
        STERN
      </text>
      {renderLights(props.arrangement.lights, cx, bowY, sternY, beam)}
    </svg>
  );
}

/**
 * Side-elevation view of the same vessel — bow to the right, stern
 * to the left, mast forward of amidships. Used to make vertical
 * stacks of all-round lights (CBD three reds, NUC two reds,
 * RAM red-white-red, trawler green-over-white, pilot white-over-red)
 * visible at a glance. The mast carries the vertical column; the
 * masthead light sits forward and high; sidelights sit at the
 * pilothouse level (only shown abstractly here — one dot stands for
 * the visible side).
 */
function SideView(props: {
  arrangement: LightArrangement;
  width: number;
}): JSX.Element {
  const w = props.width;
  const h = w * 0.55;
  // Hull baseline near the bottom; deck slightly higher; mast rises
  // above the deck on the bow side. Bow is on the right.
  const baseline = h - 6;
  const deck = h * 0.55;
  const mastBase = w * 0.6; // mast forward of midships
  const mastTop = h * 0.06;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      role="img"
      aria-label="Navigation light side elevation"
    >
      {/* Hull side silhouette: stern flat, bow rising. */}
      <path
        d={`M ${w * 0.05} ${baseline} L ${w * 0.05} ${deck} L ${w * 0.55} ${deck} L ${w * 0.7} ${deck - 6} L ${w * 0.93} ${deck - 2} L ${w * 0.95} ${baseline} Z`}
        fill="rgba(154, 166, 184, 0.18)"
        stroke="rgba(154, 166, 184, 0.55)"
        strokeWidth="1"
      />
      {/* Mast. */}
      <line
        x1={mastBase}
        y1={deck}
        x2={mastBase}
        y2={mastTop}
        stroke="rgba(154, 166, 184, 0.55)"
        strokeWidth="1"
      />
      {/* Fore yard (cross-arm) — only matters visually for the
          minesweeping triangle. Drawn faintly. */}
      <line
        x1={mastBase - w * 0.12}
        y1={mastTop + h * 0.18}
        x2={mastBase + w * 0.12}
        y2={mastTop + h * 0.18}
        stroke="rgba(154, 166, 184, 0.35)"
        strokeWidth="0.6"
      />
      <text
        x={w * 0.07}
        y={h - 1}
        fontSize="6"
        fill="var(--text-dim)"
        fontFamily="var(--font-mono)"
      >
        STERN
      </text>
      <text
        x={w - 5}
        y={h - 1}
        textAnchor="end"
        fontSize="6"
        fill="var(--text-dim)"
        fontFamily="var(--font-mono)"
      >
        BOW
      </text>
      {renderSideLights(
        props.arrangement.lights,
        mastBase,
        mastTop,
        deck,
        baseline,
        w,
        h,
      )}
    </svg>
  );
}

function renderSideLights(
  lights: ReadonlyArray<Light>,
  mastBase: number,
  mastTop: number,
  deck: number,
  baseline: number,
  w: number,
  h: number,
): JSX.Element {
  // Stack vertical all-round lights down the mast from the masthead.
  // Slot height is generous so 3 reds (CBD) read clearly.
  const slotH = h * 0.15;
  let stackY = mastTop + 2;
  const dots: JSX.Element[] = [];
  // Use a fixed render order so vertical-stack lights paint top to
  // bottom regardless of input order.
  const STACK_ORDER: Light[] = [
    "yellow-flash",
    "minesweep-mast",
    "all-round-red-top",
    "all-round-white-mid",
    "all-round-red-mid",
    "all-round-red-bottom",
    "fishing-green",
    "fishing-white",
    "pilot-white-top",
    "pilot-red-bottom",
  ];
  const present = new Set(lights);
  for (const light of STACK_ORDER) {
    if (!present.has(light)) continue;
    const colour = colourFor(light);
    dots.push(
      <g key={`${light}`}>
        <circle cx={mastBase} cy={stackY} r={3.4} fill={colour} fillOpacity="0.25" />
        <circle cx={mastBase} cy={stackY} r={2.2} fill={colour} />
      </g>,
    );
    stackY += slotH;
  }
  // Other lights at fixed positions on the side elevation.
  if (present.has("masthead")) {
    const c = colourFor("masthead");
    dots.push(
      <g key="masthead-side">
        <circle cx={mastBase + w * 0.04} cy={mastTop + h * 0.18} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={mastBase + w * 0.04} cy={mastTop + h * 0.18} r={2.2} fill={c} />
      </g>,
    );
  }
  if (present.has("stern")) {
    const c = colourFor("stern");
    dots.push(
      <g key="stern-side">
        <circle cx={w * 0.1} cy={deck - 2} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={w * 0.1} cy={deck - 2} r={2.2} fill={c} />
      </g>,
    );
  }
  // Sidelight on this elevation — pick whichever side faces the
  // viewer. We render port (red) since the side view convention is
  // looking at the vessel from her port side.
  if (present.has("port")) {
    const c = colourFor("port");
    dots.push(
      <g key="port-side">
        <circle cx={mastBase + w * 0.06} cy={deck + 2} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={mastBase + w * 0.06} cy={deck + 2} r={2.2} fill={c} />
      </g>,
    );
  }
  if (present.has("tow-yellow")) {
    const c = colourFor("tow-yellow");
    dots.push(
      <g key="tow-side">
        <circle cx={w * 0.1} cy={deck - 8} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={w * 0.1} cy={deck - 8} r={2.2} fill={c} />
      </g>,
    );
  }
  // Minesweep yard-end greens — one each at the cross-arm ends.
  if (present.has("minesweep-port-yard")) {
    const c = colourFor("minesweep-port-yard");
    dots.push(
      <g key="minesweep-port">
        <circle cx={mastBase - w * 0.12} cy={mastTop + h * 0.18} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={mastBase - w * 0.12} cy={mastTop + h * 0.18} r={2.2} fill={c} />
      </g>,
    );
  }
  if (present.has("minesweep-stbd-yard")) {
    const c = colourFor("minesweep-stbd-yard");
    dots.push(
      <g key="minesweep-stbd">
        <circle cx={mastBase + w * 0.12} cy={mastTop + h * 0.18} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={mastBase + w * 0.12} cy={mastTop + h * 0.18} r={2.2} fill={c} />
      </g>,
    );
  }
  return <>{dots}</>;
}

/**
 * Bow view — looking at the vessel coming toward you ("head-on
 * aspect" in COLREG training). The vessel's PORT (red sidelight) is
 * on YOUR RIGHT because the vessel's left side faces your right when
 * the vessel points at you. The masthead light sits high on the
 * centreline. Vertical-stack all-round lights run up the mast.
 * Yard-end greens (minesweepers) appear at the cross-arm tips.
 */
function BowView(props: {
  arrangement: LightArrangement;
  width: number;
}): JSX.Element {
  const w = props.width;
  const h = w * 1.1;
  const cx = w / 2;
  const hullTop = h * 0.55;
  const hullBottom = h - 6;
  const hullHalfBeam = w * 0.32;
  const mastTop = h * 0.06;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      role="img"
      aria-label="Bow aspect — vessel pointing at the viewer"
    >
      <text
        x={cx}
        y={6}
        fontSize="6"
        textAnchor="middle"
        fill="var(--text-dim)"
        fontFamily="var(--font-mono)"
      >
        BOW
      </text>
      {/* Hull cross-section: rounded V/U shape. */}
      <path
        d={`M ${cx - hullHalfBeam} ${hullTop} Q ${cx} ${hullBottom + 4}, ${cx + hullHalfBeam} ${hullTop} L ${cx + hullHalfBeam} ${hullTop - 4} L ${cx - hullHalfBeam} ${hullTop - 4} Z`}
        fill="rgba(154, 166, 184, 0.18)"
        stroke="rgba(154, 166, 184, 0.55)"
        strokeWidth="1"
      />
      {/* Mast — central vertical line. */}
      <line
        x1={cx}
        y1={hullTop - 4}
        x2={cx}
        y2={mastTop}
        stroke="rgba(154, 166, 184, 0.55)"
        strokeWidth="1"
      />
      {/* Fore yard (cross-arm). */}
      <line
        x1={cx - w * 0.25}
        y1={mastTop + h * 0.18}
        x2={cx + w * 0.25}
        y2={mastTop + h * 0.18}
        stroke="rgba(154, 166, 184, 0.35)"
        strokeWidth="0.6"
      />
      {/* Side labels — port/stbd from the VESSEL's perspective so
          a learner can map "vessel's port" to "viewer's right". */}
      <text
        x={cx + hullHalfBeam + 2}
        y={hullTop + 4}
        fontSize="5"
        textAnchor="start"
        fill="var(--text-dim)"
        fontFamily="var(--font-mono)"
      >
        P
      </text>
      <text
        x={cx - hullHalfBeam - 2}
        y={hullTop + 4}
        fontSize="5"
        textAnchor="end"
        fill="var(--text-dim)"
        fontFamily="var(--font-mono)"
      >
        S
      </text>
      {renderBowLights(
        props.arrangement.lights,
        cx,
        mastTop,
        hullTop,
        hullHalfBeam,
        w,
        h,
      )}
    </svg>
  );
}

function renderBowLights(
  lights: ReadonlyArray<Light>,
  cx: number,
  mastTop: number,
  hullTop: number,
  hullHalfBeam: number,
  w: number,
  h: number,
): JSX.Element {
  const slotH = h * 0.1;
  let stackY = mastTop + 2;
  const STACK_ORDER: Light[] = [
    "yellow-flash",
    "minesweep-mast",
    "all-round-red-top",
    "all-round-white-mid",
    "all-round-red-mid",
    "all-round-red-bottom",
    "fishing-green",
    "fishing-white",
    "pilot-white-top",
    "pilot-red-bottom",
  ];
  const present = new Set(lights);
  const dots: JSX.Element[] = [];
  for (const light of STACK_ORDER) {
    if (!present.has(light)) continue;
    const colour = colourFor(light);
    dots.push(
      <g key={light}>
        <circle cx={cx} cy={stackY} r={3.4} fill={colour} fillOpacity="0.25" />
        <circle cx={cx} cy={stackY} r={2.2} fill={colour} />
      </g>,
    );
    stackY += slotH;
  }
  if (present.has("masthead")) {
    const c = colourFor("masthead");
    dots.push(
      <g key="masthead-bow">
        <circle cx={cx} cy={mastTop + h * 0.18} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={cx} cy={mastTop + h * 0.18} r={2.2} fill={c} />
      </g>,
    );
  }
  // Sidelights: port red on viewer's RIGHT, stbd green on viewer's
  // LEFT (vessel facing observer).
  if (present.has("port")) {
    const c = colourFor("port");
    dots.push(
      <g key="port-bow">
        <circle cx={cx + hullHalfBeam - 2} cy={hullTop + 2} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={cx + hullHalfBeam - 2} cy={hullTop + 2} r={2.2} fill={c} />
      </g>,
    );
  }
  if (present.has("starboard")) {
    const c = colourFor("starboard");
    dots.push(
      <g key="starboard-bow">
        <circle cx={cx - hullHalfBeam + 2} cy={hullTop + 2} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={cx - hullHalfBeam + 2} cy={hullTop + 2} r={2.2} fill={c} />
      </g>,
    );
  }
  // Minesweep yard-end greens at the cross-arm tips. Position by the
  // vessel's port/starboard so they map to the bow-view convention
  // (vessel's port = viewer's right).
  if (present.has("minesweep-port-yard")) {
    const c = colourFor("minesweep-port-yard");
    const yx = cx + w * 0.25;
    const yy = mastTop + h * 0.18;
    dots.push(
      <g key="ms-port">
        <circle cx={yx} cy={yy} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={yx} cy={yy} r={2.2} fill={c} />
      </g>,
    );
  }
  if (present.has("minesweep-stbd-yard")) {
    const c = colourFor("minesweep-stbd-yard");
    const yx = cx - w * 0.25;
    const yy = mastTop + h * 0.18;
    dots.push(
      <g key="ms-stbd">
        <circle cx={yx} cy={yy} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={yx} cy={yy} r={2.2} fill={c} />
      </g>,
    );
  }
  // Anchor light — when the vessel is at anchor, single all-round
  // white forward (or two for >=50m). Show as a single white dot
  // above the bow.
  if (present.has("all-round-white")) {
    const c = colourFor("all-round-white");
    dots.push(
      <g key="anchor-bow">
        <circle cx={cx} cy={hullTop - 12} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={cx} cy={hullTop - 12} r={2.2} fill={c} />
      </g>,
    );
  }
  return <>{dots}</>;
}

/**
 * Stern view — looking at the vessel from astern (vessel going away
 * from viewer). The vessel's PORT is on YOUR LEFT now (you're behind
 * the vessel, so its left is your left). Sternlight (white) at
 * centre. Masthead is forward and effectively hidden — we don't paint
 * it. Vertical stack still visible above the mast (forward but
 * elevated). Yellow tow light sits above the sternlight when present.
 */
function SternView(props: {
  arrangement: LightArrangement;
  width: number;
}): JSX.Element {
  const w = props.width;
  const h = w * 1.1;
  const cx = w / 2;
  const hullTop = h * 0.55;
  const hullBottom = h - 6;
  const hullHalfBeam = w * 0.34;
  const mastTop = h * 0.06;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      role="img"
      aria-label="Stern aspect — vessel going away from viewer"
    >
      <text
        x={cx}
        y={6}
        fontSize="6"
        textAnchor="middle"
        fill="var(--text-dim)"
        fontFamily="var(--font-mono)"
      >
        STERN
      </text>
      {/* Hull cross-section as seen from astern: flat-bottom transom. */}
      <path
        d={`M ${cx - hullHalfBeam} ${hullTop} L ${cx + hullHalfBeam} ${hullTop} L ${cx + hullHalfBeam - 1} ${hullBottom} L ${cx - hullHalfBeam + 1} ${hullBottom} Z`}
        fill="rgba(154, 166, 184, 0.18)"
        stroke="rgba(154, 166, 184, 0.55)"
        strokeWidth="1"
      />
      <line
        x1={cx}
        y1={hullTop}
        x2={cx}
        y2={mastTop}
        stroke="rgba(154, 166, 184, 0.55)"
        strokeWidth="1"
        strokeDasharray="2 2"
      />
      <text
        x={cx - hullHalfBeam - 2}
        y={hullTop + 4}
        fontSize="5"
        textAnchor="end"
        fill="var(--text-dim)"
        fontFamily="var(--font-mono)"
      >
        P
      </text>
      <text
        x={cx + hullHalfBeam + 2}
        y={hullTop + 4}
        fontSize="5"
        textAnchor="start"
        fill="var(--text-dim)"
        fontFamily="var(--font-mono)"
      >
        S
      </text>
      {renderSternLights(
        props.arrangement.lights,
        cx,
        mastTop,
        hullTop,
        hullBottom,
        hullHalfBeam,
        h,
      )}
    </svg>
  );
}

function renderSternLights(
  lights: ReadonlyArray<Light>,
  cx: number,
  mastTop: number,
  hullTop: number,
  hullBottom: number,
  hullHalfBeam: number,
  h: number,
): JSX.Element {
  const slotH = h * 0.1;
  let stackY = mastTop + 2;
  const STACK_ORDER: Light[] = [
    "yellow-flash",
    "minesweep-mast",
    "all-round-red-top",
    "all-round-white-mid",
    "all-round-red-mid",
    "all-round-red-bottom",
    "fishing-green",
    "fishing-white",
    "pilot-white-top",
    "pilot-red-bottom",
  ];
  const present = new Set(lights);
  const dots: JSX.Element[] = [];
  for (const light of STACK_ORDER) {
    if (!present.has(light)) continue;
    const colour = colourFor(light);
    dots.push(
      <g key={light}>
        <circle cx={cx} cy={stackY} r={3.4} fill={colour} fillOpacity="0.25" />
        <circle cx={cx} cy={stackY} r={2.2} fill={colour} />
      </g>,
    );
    stackY += slotH;
  }
  // Sternlight at centre of transom.
  if (present.has("stern")) {
    const c = colourFor("stern");
    dots.push(
      <g key="stern-stern">
        <circle cx={cx} cy={hullTop + (hullBottom - hullTop) * 0.5} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={cx} cy={hullTop + (hullBottom - hullTop) * 0.5} r={2.2} fill={c} />
      </g>,
    );
  }
  // Yellow tow light above the sternlight.
  if (present.has("tow-yellow")) {
    const c = colourFor("tow-yellow");
    dots.push(
      <g key="tow-stern">
        <circle cx={cx} cy={hullTop + 4} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={cx} cy={hullTop + 4} r={2.2} fill={c} />
      </g>,
    );
  }
  // Sidelights — from astern, vessel's port (red) is on viewer's
  // LEFT. Painted at hull edges; the visibility arcs say a sidelight
  // isn't actually seen from dead astern, but for an instructional
  // diagram we show position regardless.
  if (present.has("port")) {
    const c = colourFor("port");
    dots.push(
      <g key="port-stern">
        <circle cx={cx - hullHalfBeam + 2} cy={hullTop + 2} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={cx - hullHalfBeam + 2} cy={hullTop + 2} r={2.2} fill={c} />
      </g>,
    );
  }
  if (present.has("starboard")) {
    const c = colourFor("starboard");
    dots.push(
      <g key="starboard-stern">
        <circle cx={cx + hullHalfBeam - 2} cy={hullTop + 2} r={3} fill={c} fillOpacity="0.25" />
        <circle cx={cx + hullHalfBeam - 2} cy={hullTop + 2} r={2.2} fill={c} />
      </g>,
    );
  }
  return <>{dots}</>;
}

function hullPath(cx: number, bowY: number, sternY: number, beam: number): string {
  const halfBeam = beam / 2;
  const noseY = bowY + 4;
  // Smooth-bow elliptical hull.
  return [
    `M ${cx} ${bowY}`,
    `Q ${cx + halfBeam} ${noseY + 2}, ${cx + halfBeam} ${noseY + 8}`,
    `L ${cx + halfBeam} ${sternY - 2}`,
    `Q ${cx + halfBeam} ${sternY}, ${cx + halfBeam - 2} ${sternY}`,
    `L ${cx - halfBeam + 2} ${sternY}`,
    `Q ${cx - halfBeam} ${sternY}, ${cx - halfBeam} ${sternY - 2}`,
    `L ${cx - halfBeam} ${noseY + 8}`,
    `Q ${cx - halfBeam} ${noseY + 2}, ${cx} ${bowY}`,
    "Z",
  ].join(" ");
}

function SidelightArcs(props: {
  cx: number;
  cy: number;
  radius: number;
}): JSX.Element {
  // Starboard arc: dead ahead (0°) to 22.5° abaft starboard beam (112.5°).
  // Port arc: dead ahead → counter-clockwise to 247.5°. Both painted as
  // faint wedges so the visibility footprint is obvious without
  // dominating.
  return (
    <>
      <Wedge
        cx={props.cx}
        cy={props.cy}
        radius={props.radius}
        startDeg={-90}
        endDeg={22.5}
        fill="rgba(34, 197, 94, 0.12)"
      />
      <Wedge
        cx={props.cx}
        cy={props.cy}
        radius={props.radius}
        startDeg={-112.5}
        endDeg={-90}
        fill="rgba(239, 68, 68, 0.12)"
      />
    </>
  );
}

function Wedge(props: {
  cx: number;
  cy: number;
  radius: number;
  startDeg: number;
  endDeg: number;
  fill: string;
}): JSX.Element {
  const start = polar(props.cx, props.cy, props.radius, props.startDeg);
  const end = polar(props.cx, props.cy, props.radius, props.endDeg);
  const sweep = props.endDeg - props.startDeg;
  const large = Math.abs(sweep) > 180 ? 1 : 0;
  return (
    <path
      d={`M ${props.cx} ${props.cy} L ${start.x} ${start.y} A ${props.radius} ${props.radius} 0 ${large} 1 ${end.x} ${end.y} Z`}
      fill={props.fill}
    />
  );
}

function polar(
  cx: number,
  cy: number,
  r: number,
  deg: number,
): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/**
 * Position each light correctly on the plan view. Vertical-stack
 * lights (red/red over reds for NUC/RAM/CBD, trawler green-over-
 * white) are placed on the centreline above the mast, offset so they
 * read as a vertical column even though the mast itself is one
 * point on the deck.
 */
function renderLights(
  lights: ReadonlyArray<Light>,
  cx: number,
  bowY: number,
  sternY: number,
  beam: number,
): JSX.Element {
  const halfBeam = beam / 2;
  const mastY = bowY + (sternY - bowY) * 0.35;
  const stackTop = bowY + 4;
  const dots: JSX.Element[] = [];
  // Track stack position above the mast for vertically-arranged
  // lights. Each one shifts the next slot upward.
  let stackY = stackTop;
  const stackSlot = 6;

  for (const light of lights) {
    let cx0 = cx;
    let cy0 = mastY;
    let r = 3;
    switch (light) {
      case "port":
        cx0 = cx - halfBeam - 1;
        cy0 = bowY + (sternY - bowY) * 0.4;
        r = 3;
        break;
      case "starboard":
        cx0 = cx + halfBeam + 1;
        cy0 = bowY + (sternY - bowY) * 0.4;
        r = 3;
        break;
      case "stern":
        cx0 = cx;
        cy0 = sternY + 1;
        r = 3;
        break;
      case "masthead":
        // Forward of the mast, centred.
        cx0 = cx;
        cy0 = bowY + (sternY - bowY) * 0.18;
        r = 3.4;
        break;
      case "yellow-flash":
        cx0 = cx;
        cy0 = stackY;
        stackY += stackSlot;
        r = 3.4;
        break;
      case "tow-yellow":
        // Yellow tow light sits over the stern.
        cx0 = cx;
        cy0 = sternY - 6;
        r = 3;
        break;
      case "all-round-white":
        // Anchor light — single forward all-round.
        cx0 = cx;
        cy0 = bowY + (sternY - bowY) * 0.25;
        r = 3.4;
        break;
      case "fishing-green":
      case "fishing-white":
      case "all-round-red-top":
      case "all-round-red-mid":
      case "all-round-red-bottom":
      case "all-round-white-mid":
      case "pilot-white-top":
      case "pilot-red-bottom":
      case "minesweep-mast":
        // All vertically-stacked all-round lights share a column on
        // the centreline above the mast.
        cx0 = cx;
        cy0 = stackY;
        stackY += stackSlot;
        r = 3;
        break;
      case "dredger-obstructed-top":
        // Two reds on the obstructed side (assume port).
        cx0 = cx - halfBeam - 4;
        cy0 = mastY - 4;
        r = 3;
        break;
      case "dredger-obstructed-bottom":
        cx0 = cx - halfBeam - 4;
        cy0 = mastY + 4;
        r = 3;
        break;
      case "dredger-clear-top":
        // Two greens on the clear side (assume starboard).
        cx0 = cx + halfBeam + 4;
        cy0 = mastY - 4;
        r = 3;
        break;
      case "dredger-clear-bottom":
        cx0 = cx + halfBeam + 4;
        cy0 = mastY + 4;
        r = 3;
        break;
      case "minesweep-port-yard":
        // Greens at each end of the fore-yard cross-arm.
        cx0 = cx - halfBeam - 2;
        cy0 = bowY + (sternY - bowY) * 0.18;
        r = 3;
        break;
      case "minesweep-stbd-yard":
        cx0 = cx + halfBeam + 2;
        cy0 = bowY + (sternY - bowY) * 0.18;
        r = 3;
        break;
    }
    const colour = colourFor(light);
    dots.push(
      <g key={`${light}-${dots.length}`}>
        <circle cx={cx0} cy={cy0} r={r + 2} fill={colour} fillOpacity="0.25" />
        <circle cx={cx0} cy={cy0} r={r} fill={colour} />
      </g>,
    );
  }
  return <>{dots}</>;
}
